import React, { createContext, useContext, useState, useEffect } from 'react';

// Create context with default values
const OnboardingContext = createContext({
    currentStep: 1,
    userData: {},
    companySettings: {},
    setStepValid: () => {},
    goToNextStep: () => {},
    goToPreviousStep: () => {},
    updateUser: () => {},
});

// Provider component
export const OnboardingProvider = ({ children, initialStep = 1, initialUserData = {}, initialCompanySettings = {} }) => {
    const [currentStep, setCurrentStep] = useState(initialStep);
    const [userData, setUserData] = useState(initialUserData);
    const [companySettings, setCompanySettings] = useState(initialCompanySettings);
    const [isStepValid, setIsStepValid] = useState(true);

    // Update state when props change
    useEffect(() => {
        setCurrentStep(initialStep);
    }, [initialStep]);

    useEffect(() => {
        setUserData(initialUserData);
    }, [initialUserData]);

    useEffect(() => {
        setCompanySettings(initialCompanySettings);
    }, [initialCompanySettings]);

    // Update wizard data in window global if window exists (for browser environments)
    useEffect(() => {
        if (typeof window !== 'undefined' && window.__WIZARD_DATA__) {
            window.__WIZARD_DATA__.user = userData;
        }
    }, [userData]);

    // Function to go to next step
    const goToNextStep = () => {
        if (isStepValid) {
            // Check if parent has provided a custom goToNextStep
            if (window.onboardingGoToNextStep) {
                return window.onboardingGoToNextStep();
            }

            setCurrentStep(prevStep => prevStep + 1);

            // Add URL history update for browser navigation
            if (typeof window !== 'undefined') {
                localStorage.setItem('findbob_onboarding_step', currentStep + 1);
            }
            return true;
        }
        return false;
    };

    // Function to go to previous step
    const goToPreviousStep = () => {
        if (currentStep > 1) {
            // Check if parent has provided a custom goToPreviousStep
            if (window.onboardingGoToPreviousStep) {
                return window.onboardingGoToPreviousStep();
            }

            setCurrentStep(prevStep => prevStep - 1);

            // Add URL history update for browser navigation
            if (typeof window !== 'undefined') {
                localStorage.setItem('findbob_onboarding_step', currentStep - 1);
            }
            return true;
        }
        return false;
    };

    // Function to update user data
    const updateUser = (newData) => {
        // Check if parent has provided a custom updateUser
        if (window.onboardingUpdateUser) {
            return window.onboardingUpdateUser(newData);
        }

        setUserData(prevData => ({...prevData, ...newData }));
        return true;
    };

    // Set step validity
    const setStepValid = (isValid) => {
        setIsStepValid(isValid);
    };

    // Context value
    const contextValue = {
        currentStep,
        userData,
        companySettings,
        setStepValid,
        goToNextStep,
        goToPreviousStep,
        updateUser,
        isStepValid,
    };

    return ( <
        OnboardingContext.Provider value = { contextValue } > { children } <
        /OnboardingContext.Provider>
    );
};

// Custom hook to use the context
export const useOnboardingState = () => useContext(OnboardingContext);

export default OnboardingContext;